import Page from 'modules/app/components/Page';
import s from './Product.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import useGetProductById from '../../../../../sdk/hooks/shop/useGetProductById';
import useGetSalonById from 'sdk/hooks/salon/useGetSalonById';
import useGetStoreById from 'sdk/hooks/shop/useGetStoreById';
import ArrowDownGray from '../../../../../images/new-icons/arrow-down-gray.svg';
import ArrowDownBlack from '../../../../../images/arrow-down-w.svg';
import Button from 'components/shared/Button';
import useIsAuth from 'sdk/hooks/useIsAuth';
import { useEffect, useRef, useState } from 'react';
import Reviews from 'modules/product/pages/Reviews/Reviews';
import useGetProductVariationsByProductId from 'sdk/hooks/shop/useGetProductVariationsByProductId';
import { useTypedSelector } from 'store/useTypedSelector';
import { useActions } from 'store/useActions';
import ShoppingCart from 'modules/shop/pages/ShoppingCart';
import TopMenuForProduct from './TopMenuForProduct';
import { ReactComponent as HeartEmpty } from '../../../../../images/new-icons/heard-dark-green-empty.svg';
import { ReactComponent as HeartFull } from '../../../../../images/new-icons/heart-dark-green-filled.svg';
import { ReactComponent as ShareIcon } from '../../../../../images/new-icons/share-filled-dark.svg';
import { ReactComponent as RiyalIcon } from '../../../../../images/new-icons/riyal.svg';
import useIsProductFavorite from 'sdk/hooks/shop/useIsProductFavorite';
import useToggleProductFavorite from 'sdk/hooks/shop/useToggleProductFavorite';
import useGetProductReviewsByProductId from 'sdk/hooks/shop/useGetProductReviewsByProductId';
import { useProductCounter } from 'sdk/hooks/shop/useProductCounter';
import { useTranslation } from 'react-i18next';
import BtnGotoSomewhere from 'modules/shop/pages/ShoppingCart/BtnGotoSomewhere';

const Product = () => {
  const { t } = useTranslation() as any;

  const navigate = useNavigate();
  const { id: idString } = useParams();
  const id = parseInt(idString || '', 10);
  const { data: product } = useGetProductById(id);
  const { data: salon } = useGetSalonById(product?.salon);
  const { data: storeOwner } = useGetStoreById(product?.store_id);
  const { data: productVariations } = useGetProductVariationsByProductId(product?.id);

  // Determine which seller to display (store owner takes priority)
  const sellerAvatar = storeOwner?.avatar || salon?.avatar;
  const sellerName = storeOwner?.store_name || salon?.name;
  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const { productCount, isLoading, handleIncrement, handleDecrement, handleAddToBag } =
    useProductCounter(id, userId, product?.price);
  const { setIsOpenBasket, setIsButtonMenuVisible } = useActions();
  const { isOpenBasket } = useTypedSelector(state => state.postSlice);
  const { data: isFavorited } = useIsProductFavorite(id, userId);
  const { mutate: toggleFavorite } = useToggleProductFavorite();
  const { data: reviews, refetch: refetchProductReviews } = useGetProductReviewsByProductId(id);

  const [showInformation, setShowInformation] = useState(false);
  const [showIngredients, setShowIngredients] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(productVariations?.[0]);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  const [carouselIdx, setCarouselIdx] = useState<number>(0);

  const sortedImages =
    product?.images && product.images.length > 0
      ? [...product.images].sort((a, b) => a.sort_order - b.sort_order)
      : product?.image
      ? [{ id: 0, image: product.image, sort_order: 0 }]
      : [];

  const hasMultipleImages = sortedImages?.length > 1;

  const xTouch = useRef<number>(0);
  const [dragDiff, setDragDiff] = useState<number>(0);

  const handleOnTouchStart = (e: React.TouchEvent) => {
    xTouch.current = e.touches[0].clientX;
    setDragDiff(0);
  };

  const handleOnTouchMove = (e: React.TouchEvent) => {
    const diff = e.touches[0].clientX - xTouch.current;
    setDragDiff(diff);
  };

  const handleOnTouchEnd = (e: React.TouchEvent) => {
    const diff = e.changedTouches[0].clientX - xTouch.current;
    const treshold = 20;
    if (diff <= treshold) {
      if (carouselIdx === product?.images?.length - 1) setCarouselIdx(0);
      else setCarouselIdx(prev => prev + 1);
    } else if (diff >= -treshold) {
      if (carouselIdx === 0) setCarouselIdx(product?.images?.length - 1);
      else setCarouselIdx(prev => prev - 1);
    }

    setDragDiff(0);
  };

  useEffect(() => {
    if (productVariations?.length && !selectedVariation) {
      setSelectedVariation(productVariations[0]);
    }
  }, [productVariations, selectedVariation]);

  useEffect(() => {
    if (productVariations?.length && !selectedColor) {
      setSelectedColor(productVariations[0].color);
    }
  }, [productVariations, selectedColor]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleVariationSelect = (variation: any) => {
    setSelectedVariation(variation);
    setShowSizeDropdown(false);
  };

  const handleFavorite = () => {
    if (!userId) {
      navigate('/sign-up');
      return;
    }

    toggleFavorite(
      { productId: id, userId },
      {
        onSuccess: () => {},
        onError: error => {
          console.error('Error toggling favorite:', error);
        },
      },
    );
  };

  const handleColorSelect = (color: string | null) => {
    setSelectedColor(color);
    setShowColorDropdown(false);
  };

  const handleShare = () => {
    navigator.share({
      title: 'Share',
      text: 'Share',
      url: window.location.href,
    });
  };

  useEffect(() => {
    refetchProductReviews();
  }, [refetchProductReviews]);

  useEffect(() => {
    setIsButtonMenuVisible(true);
  }, [setIsButtonMenuVisible]);

  return (
    <div style={{ overflow: 'auto' }}>
      <Page>
        <div className={s.container}>
          <TopMenuForProduct handleBack={handleBack} openBasket={() => setIsOpenBasket(false)} />

          <div className={s.carousel_wrapper}>
            {hasMultipleImages ? (
              <>
                <div
                  onTouchStart={handleOnTouchStart}
                  onTouchMove={handleOnTouchMove}
                  onTouchEnd={handleOnTouchEnd}
                  className={s.carousel_inner}
                  style={{
                    transform: `translateX(-${carouselIdx * 100}%) translateX(${dragDiff}px)`,
                    transition: dragDiff === 0 ? 'transform 0.3s ease' : 'none',
                  }}
                >
                  {sortedImages.map(img => (
                    <img key={img.id} className={s.product_img} src={img.image} alt="product" />
                  ))}
                </div>
                <div className={s.carousel_bullets}>
                  {sortedImages.map((_, idx) => (
                    <div key={idx} className={idx === carouselIdx ? s.bullet_act : s.bullet} />
                  ))}
                </div>
              </>
            ) : (
              <img className={s.product_img} src={sortedImages[0]?.image} alt="product" />
            )}
          </div>

          <div className={s.product_info}>
            <div className={s.product_info_title}>{product?.name}</div>

            <div className={s.product_info_price_wrapper}>
              <span className={s.product_info_current_price}>
                <RiyalIcon className={s.riyal_icon} /> {product?.price?.toFixed(2)}
              </span>
              {product?.original_price && product.original_price > product.price && (
                <span className={s.product_info_original_price}>
                  <span className={s.currency_symbol}>
                    <RiyalIcon className={s.riyal_icon} />
                  </span>
                  <span className={s.price_value}>{product.original_price.toFixed(2)}</span>
                </span>
              )}
            </div>
          </div>


          <div className={s.product_add_to_bag}>
            <Button
              className={s.product_add_to_bag_button}
              wide
              darkGreen
              loading={isLoading}
              onClick={productCount === 0 ? handleAddToBag : undefined}
              count={productCount}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              showCounter={productCount > 0}
            >
              {productCount === 0 ? t('Add to cart') : ''}
            </Button>

            {/* <BtnGotoSomewhere link={() => setIsOpenBasket(false)} title={`Go to Cart`} /> */}

             <button className={s.product_add_to_bag_favorite} onClick={handleFavorite}>
              {isFavorited ? <HeartFull /> : <HeartEmpty />}
            </button>

            <button className={s.product_add_to_bag_favorite} onClick={handleShare}>
              <ShareIcon />
            </button>
          </div>

          <div className={s.product_attributes}>
            {!!selectedVariation &&
              selectedVariation.amount !== undefined &&
              selectedVariation.amount !== null && (
                <div className={s.product_attributes_item}>
                  <div className={s.product_attributes_item_title}>Size:</div>
                  <div
                    className={s.product_attributes_item_value}
                    onClick={() => setShowSizeDropdown(!showSizeDropdown)}
                  >
                    {selectedVariation?.amount} {selectedVariation?.measure?.name || 'N/A'}
                    <img src={ArrowDownGray} alt="ArrowDownGray" />
                  </div>
                  {showSizeDropdown && (
                    <div className={s.dropdown_menu}>
                      {productVariations?.map((variation: any) => (
                        <div
                          key={variation.id}
                          className={s.dropdown_item}
                          onClick={() => handleVariationSelect(variation)}
                        >
                          {variation.amount} {variation.measure?.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            {selectedColor != '{}' && (
              <div className={s.product_attributes_item}>
                <div className={s.product_attributes_item_title2}>Color:</div>
                <div
                  className={s.product_attributes_item_value}
                  onClick={() => setShowColorDropdown(!showColorDropdown)}
                >
                  {selectedColor != '{}' ? selectedColor || 'Select color' : ''}{' '}
                  <img src={ArrowDownGray} alt="ArrowDownGray" />
                </div>
                {showColorDropdown && (
                  <div className={s.dropdown_menu}>
                    {Array.from(new Set(productVariations?.map(v => v.color)))
                      .filter((c): c is string => Boolean(c))
                      .map(color => (
                        <div
                          key={color}
                          className={s.dropdown_item}
                          onClick={() => handleColorSelect(color)}
                        >
                          {color}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {product?.description != null && product?.description != '{}' && (
            <p className={s.product_description}>{product?.description}</p>
          )}
          <div className={s.product_seller}>
            <img className={s.product_seller_logo} src={sellerAvatar} alt="seller logo" />
            <div className={s.product_seller_info}>
              <div className={s.product_seller_title}>{sellerName}</div>
              <div className={s.product_seller_seller}>{t('Seller')}</div>
            </div>
          </div>

          {product?.brand && (
            <div className={s.product_brand}>
              <div className={s.product_brand_title}>{product?.brand?.name}</div>
              <div className={s.product_brand_brand}> {t('Brand')}</div>
            </div>
          )}

          {product?.instruction && (
            <>
              {' '}
              <div className={s.product_information}>
                <div className={s.product_information_title}> {t('How to use')}</div>
                <img
                  className={`${s.product_information_arrow} ${showInformation ? s.rotated : ''}`}
                  src={ArrowDownBlack}
                  onClick={() => setShowInformation(!showInformation)}
                  alt="ArrowDownBlack"
                />
              </div>
              {showInformation && (
                <div className={s.product_information_content}>
                  <p className={s.product_description}>{product?.instruction}</p>
                </div>
              )}
            </>
          )}
          {product?.ingredients && (
            <>
              <div className={s.product_information}>
                <div className={s.product_information_title}> {t('Key Ingredients')}</div>
                <img
                  className={`${s.product_information_arrow} ${showIngredients ? s.rotated : ''}`}
                  src={ArrowDownBlack}
                  onClick={() => setShowIngredients(!showIngredients)}
                  alt="ArrowDownBlack"
                />
              </div>
              {showIngredients && (
                <div className={s.product_information_content}>
                  <p className={s.product_description}>{product?.ingredients}</p>
                </div>
              )}
            </>
          )}

          <>
            <div className={s.product_information}>
              <div className={s.product_information_title}>{t('Reviews')}</div>
              <img
                className={`${s.product_information_arrow} ${showReviews ? s.rotated : ''}`}
                src={ArrowDownBlack}
                onClick={() => setShowReviews(!showReviews)}
                alt="ArrowDownBlack"
              />
            </div>
            {showReviews &&
              (reviews?.length ? (
                reviews?.map((review: any) => (
                  <Reviews
                    key={review?.id}
                    rank={review?.rank}
                    text={review?.text}
                    userName={review?.name}
                    userAvatar={review?.user?.avatar}
                    media={review?.media}
                    likes={review?.likes_amount}
                    reviewId={review?.id}
                    refetchProductReviews={refetchProductReviews}
                  />
                ))
              ) : (
                <div className={s.noReviews}>{t('There are no reviews yet')}</div>
              ))}
          </>

          <ShoppingCart open={isOpenBasket} setOpenShoppingCartMenu={setIsOpenBasket} />
        </div>
      </Page>
    </div>
  );
};

export default Product;
